import { Component, effect, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import { Impediment, ImpedimentGroup } from '../../../api/app-types/impediment.app';
import { IMPEDIMENT_PROVIDER } from '../../../api/services/impediment.service';
import { LvrClientReport } from '../../../core/components/client-report/client-report';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrHtmlEditor } from '../../../core/components/html-editor/html-editor';
import { mergeOriginalTemplateWithEditedContent } from '../../../core/components/html-editor/template.func';
import { ImpedimentList } from '../../../core/components/impediment-list/impediment-list';
import { LvrSection } from '../../../core/components/section/section';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { validateIfIndicatorsAreSet } from '../../../shared/validators/editor-report-indicators.validator';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-end-report',
	templateUrl: './end-report.html',
	styleUrl: './end-report.scss',
	imports: [
		TranslocoPipe,
		LvrHeading,
		LvrHtmlEditor,
		MatTooltip,
		LvrDefaultActions,
		ImpedimentList,
		ReactiveFormsModule,
		LvrSection,
		LvrButton,
	],
})
export class LvrEndReport extends LvrClientReport {
	private readonly fb = inject(FormBuilder);
	private readonly impedimentApi = inject(IMPEDIMENT_PROVIDER);
	public readonly availableImpediments = this.resolveDataAsSignal<ImpedimentGroup[]>('impedimentGroups');
	private readonly impedimentsOnReportPage =
		this.resolveDataAsSignal<PaginatedResponse<Impediment>>('impedimentsOnReportPage');

	public readonly form = this.fb.group({
		impediments: this.fb.control<number[] | null>(null),
	});

	private readonly END_REPORT_SAVE_LOADING_KEY = `end-report-${this.assignmentId()}.save`;
	public readonly isLoadingForSaveEndReport = this.loading.isCurrentlyLoading(
		this.END_REPORT_SAVE_LOADING_KEY,
		'requestKey',
	);

	public constructor() {
		super();
		effect(() => {
			if (!this.impedimentsOnReportPage() || this.impedimentsOnReportPage()!.entities.length < 1) return;

			const impedimentIds = this.impedimentsOnReportPage()!.entities.map((i) => i.id);
			this.form.get('impediments')!.patchValue(impedimentIds);
			this.form.get('impediments')!.updateValueAndValidity();
		});
	}

	public async save() {
		if (!this.canEditReport()) return;

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), this.END_REPORT_SAVE_LOADING_KEY);
		const report = this.report()!;
		const template = report.template;

		if (!template) return;

		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		const content = mergeOriginalTemplateWithEditedContent(template.htmlContent, this.updateHtmlContent());

		const impedimentIds = this.form.value.impediments || [];
		const hasIndicators = validateIfIndicatorsAreSet(content);
		const hasValidImpediments = this.form.controls.impediments.valid;

		try {
			await this.impedimentApi.saveImpedimentsToReport(report.id, impedimentIds);

			await this.api.updateReportDocument({
				assignmentId: this.assignmentId()!,
				htmlContent: content,
				reportId: report.id,
				isFinal: false,
				isDraft: !(hasIndicators && hasValidImpediments),
				reportDocumentTemplateId: template.id,
			});

			await this.navigateAfterFinished(this.getAssignmentDetailsCommands('../..'), {
				relativeTo: this.route,
			});

			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.save_report_success')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.save_report_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}
}
